import { useState, useMemo } from "react";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAIToolsCatalog } from '@/hooks/useAIToolsCatalog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { 
  Wrench, DollarSign, Users, TrendingUp, Brain, Database, Layers, AppWindow, Loader2, Server
} from "lucide-react";
import { OrganizationFilter, useOrganizationFilter } from '@/components/admin/OrganizationFilter';
import { useAuth } from '@/contexts/AuthContext';
import {
  useToolLicenses, useDeleteToolLicense,
  useAIModels, useDeleteAIModel,
  useDataSources, useDeleteDataSource,
  useOwnSolutions, useDeleteOwnSolution,
  useEnterpriseSoftware, useDeleteEnterpriseSoftware,
  useSolutionProjectsBatch, useOrgProjects,
} from '@/hooks/useAIInventory';

import ToolsTab from '@/components/admin/ai-inventory/ToolsTab';
import ModelsTab from '@/components/admin/ai-inventory/ModelsTab';
import DataSourcesTab from '@/components/admin/ai-inventory/DataSourcesTab';
import SolutionsTab from '@/components/admin/ai-inventory/SolutionsTab';
import SoftwareTab from '@/components/admin/ai-inventory/SoftwareTab';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';

export default function AIInventoryPage() {
  const { profile } = useAuth();
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();
  const { isSuperAdmin, effectiveOrg, showAllOrgs, getOrgName } = useOrganizationFilter(selectedOrg);
  
  const orgIdForQueries = effectiveOrg || profile?.organization_id || 'all';

  // Dynamic catalog from DB
  const { data: catalogToolsRaw = [] } = useAIToolsCatalog();
  const aiToolCatalog = useMemo(() => catalogToolsRaw.map(t => ({
    name: t.name, vendor: t.vendor || '', category: t.category || '',
  })), [catalogToolsRaw]);

  // Business areas
  const { data: businessAreas = [] } = useQuery({
    queryKey: ['business-areas'],
    queryFn: async () => {
      const { data, error } = await supabase.from('business_areas').select('name').order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Data queries
  const { data: tools = [], isLoading: toolsLoading } = useToolLicenses(orgIdForQueries);
  const { data: aiModels = [], isLoading: modelsLoading } = useAIModels(orgIdForQueries);
  const { data: dataSources = [], isLoading: dataSourcesLoading } = useDataSources(orgIdForQueries);
  const { data: solutions = [], isLoading: solutionsLoading } = useOwnSolutions(orgIdForQueries);
  const { data: enterpriseSw = [], isLoading: swLoading } = useEnterpriseSoftware(orgIdForQueries);
  const { data: orgProjects = [] } = useOrgProjects(orgIdForQueries);
  
  const solutionIds = useMemo(() => solutions.map((s: any) => s.id), [solutions]);
  const { data: solutionProjectsMap = {} } = useSolutionProjectsBatch(solutionIds);

  // Delete mutations
  const deleteTool = useDeleteToolLicense();
  const deleteModel = useDeleteAIModel();
  const deleteDataSourceMut = useDeleteDataSource();
  const deleteSolution = useDeleteOwnSolution();
  const deleteSoftware = useDeleteEnterpriseSoftware();

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string; name: string } | null>(null);

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) return;
    const { type, id } = deleteConfirm;
    switch (type) {
      case 'tool': deleteTool.mutate(id); break;
      case 'model': deleteModel.mutate(id); break;
      case 'datasource': deleteDataSourceMut.mutate(id); break;
      case 'solution': deleteSolution.mutate(id); break;
      case 'software': deleteSoftware.mutate(id); break;
    }
    setDeleteConfirm(null);
  };

  // Summary metrics
  const totalToolsCost = tools.reduce((sum: number, t: any) => sum + (t.total_cost || 0), 0);
  const totalLicenses = tools.reduce((sum: number, t: any) => sum + (t.licenses_contracted || 0), 0);
  const usedLicenses = tools.reduce((sum: number, t: any) => sum + (t.licenses_used || 0), 0);
  const productionSolutions = solutions.filter((s: any) => s.status === "active").length;
  const productionModels = aiModels.filter((m: any) => m.status === "production").length;
  const aiIntegratedApps = enterpriseSw.filter((s: any) => s.integration_with_ai).length;

  const isLoading = toolsLoading || modelsLoading || dataSourcesLoading || solutionsLoading || swLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Inventario de IA</h1>
          <p className="text-muted-foreground">
            {isSuperAdmin && showAllOrgs 
              ? 'Inventario de todas las organizaciones' 
              : `Inventario de ${getOrgName() || 'tu organización'}`}
          </p>
        </div>
        <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Summary Cards - 2 rows of 3 */}
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Herramientas Activas</CardTitle>
                <Wrench className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{tools.filter((t: any) => t.status === "active").length}</div>
                <p className="text-xs text-muted-foreground">{tools.length} total</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Costo Mensual</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${totalToolsCost.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">{totalLicenses} licencias</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Uso de Licencias</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalLicenses > 0 ? Math.round((usedLicenses / totalLicenses) * 100) : 0}%</div>
                <Progress value={totalLicenses > 0 ? (usedLicenses / totalLicenses) * 100 : 0} className="mt-2" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Modelos en Producción</CardTitle>
                <Brain className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{productionModels}</div>
                <p className="text-xs text-muted-foreground">{aiModels.length} total</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Fuentes de Datos</CardTitle>
                <Server className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dataSources.length}</div>
                <p className="text-xs text-muted-foreground">{[...new Set(dataSources.map((d: any) => d.type))].length} tipos</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Apps con IA</CardTitle>
                <AppWindow className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{aiIntegratedApps}</div>
                <p className="text-xs text-muted-foreground">{enterpriseSw.length} apps total</p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="tools" className="space-y-4">
            <TabsList className="flex-wrap">
              <TabsTrigger value="tools" className="gap-2"><Wrench className="h-4 w-4" />Herramientas IA</TabsTrigger>
              <TabsTrigger value="models" className="gap-2"><Brain className="h-4 w-4" />Modelos IA</TabsTrigger>
              <TabsTrigger value="datasources" className="gap-2"><Database className="h-4 w-4" />Fuentes de Datos</TabsTrigger>
              <TabsTrigger value="solutions" className="gap-2"><Layers className="h-4 w-4" />Soluciones</TabsTrigger>
              <TabsTrigger value="ecosystem" className="gap-2"><AppWindow className="h-4 w-4" />Aplicaciones</TabsTrigger>
            </TabsList>

            <TabsContent value="tools" className="space-y-4">
              <ToolsTab
                tools={tools}
                aiToolCatalog={aiToolCatalog}
                orgId={orgIdForQueries}
                isSuperAdmin={isSuperAdmin}
                selectedOrg={selectedOrg}
                profileOrgId={profile?.organization_id}
                onDeleteConfirm={setDeleteConfirm}
              />
            </TabsContent>

            <TabsContent value="models" className="space-y-4">
              <ModelsTab
                aiModels={aiModels}
                dataSources={dataSources}
                businessAreas={businessAreas}
                orgId={orgIdForQueries}
                isSuperAdmin={isSuperAdmin}
                selectedOrg={selectedOrg}
                profileOrgId={profile?.organization_id}
                onDeleteConfirm={setDeleteConfirm}
              />
            </TabsContent>

            <TabsContent value="datasources" className="space-y-4">
              <DataSourcesTab
                dataSources={dataSources}
                enterpriseSw={enterpriseSw}
                businessAreas={businessAreas}
                aiToolCatalog={aiToolCatalog}
                orgId={orgIdForQueries}
                isSuperAdmin={isSuperAdmin}
                selectedOrg={selectedOrg}
                profileOrgId={profile?.organization_id}
                onDeleteConfirm={setDeleteConfirm}
              />
            </TabsContent>

            <TabsContent value="solutions" className="space-y-4">
              <SolutionsTab
                solutions={solutions}
                solutionProjectsMap={solutionProjectsMap}
                orgProjects={orgProjects}
                businessAreas={businessAreas}
                aiToolCatalog={aiToolCatalog}
                orgId={orgIdForQueries}
                isSuperAdmin={isSuperAdmin}
                selectedOrg={selectedOrg}
                profileOrgId={profile?.organization_id}
                onDeleteConfirm={setDeleteConfirm}
              />
            </TabsContent>

            <TabsContent value="ecosystem" className="space-y-4">
              <SoftwareTab
                enterpriseSw={enterpriseSw}
                dataSources={dataSources}
                businessAreas={businessAreas}
                aiToolCatalog={aiToolCatalog}
                orgId={orgIdForQueries}
                isSuperAdmin={isSuperAdmin}
                selectedOrg={selectedOrg}
                profileOrgId={profile?.organization_id}
                onDeleteConfirm={setDeleteConfirm}
              />
            </TabsContent>
          </Tabs>

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar "{deleteConfirm?.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. El registro será eliminado permanentemente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
